import { useParams, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { SubmitHandler } from "react-hook-form";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import useGetDailyFormVehicle from "hooks/fleet/useGetDailyFormVehicle";
import useUpdateDailyFormVehicle from "hooks/fleet/useUpdateDailyFormVehicle";
import AbsoluteCenterProgressLoader from "components/AbsoluteCenterProgressLoader/AbsoluteCenterProgressLoader";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";
import VehicleForm, { VehicleFormData } from "components/fleet/VehicleForm";
import { getDefaultValues } from "DDD/action-objects/VehicleEdit";
import VehiclePartsSection from "components/fleet/VehiclePartsSection";
import VehicleServicePlansSection from "components/fleet/VehicleServicePlansSection";
import VehicleMaintenanceHistorySection from "components/fleet/VehicleMaintenanceHistorySection";
import { Grid } from "@mui/material";

export default function EditVehicle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: vehicle,
    loading: vehicleLoading,
    error: vehicleError,
    refetch,
  } = useGetDailyFormVehicle({
    variables: { id: id! },
  });

  const [updateVehicle, { loading: updating }] = useUpdateDailyFormVehicle();

  const onSubmit: SubmitHandler<VehicleFormData> = async (data: VehicleFormData) => {
    if (!id) return;

    const input = {
      id,
      name: data.name,
      dailyFormVehicleTypeId: data.dailyFormVehicleTypeId,
      year: data.year,
      make: data.make || null,
      model: data.model || null,
      vin: data.vin || null,
      licensePlate: data.licensePlate || null,
      registrationState: data.registrationState || null,
      registrationExpirationDate: data.registrationExpirationDate || null,
      inServiceDate: data.inServiceDate || null,
      meterType: data.meterType || null,
      currentMeterReading: data.currentMeterReading ?? null,
      defaultServiceIntervalValue: data.defaultServiceIntervalValue ?? null,
      defaultServiceIntervalUnit: data.defaultServiceIntervalUnit || null,
      notes: data.notes || null,
      isActive: data.isActive,
    };

    const result = await updateVehicle({
      variables: {
        input,
      },
    });

    if (result && !result.errors) {
      navigate("/fleet/fleet-settings");
    }
  };

  const handleCancel = () => {
    navigate("/fleet/fleet-settings");
  };

  const initialData = useMemo(() => {
    if (!vehicle) return undefined;

    return getDefaultValues({
      name: vehicle.name,
      dailyFormVehicleTypeId: vehicle.dailyFormVehicleType?.id || null,
      year: vehicle.year ?? null,
      make: vehicle.make || "",
      model: vehicle.model || "",
      vin: vehicle.vin || "",
      licensePlate: vehicle.licensePlate || "",
      registrationState: vehicle.registrationState || "",
      registrationExpirationDate: vehicle.registrationExpirationDate || null,
      inServiceDate: vehicle.inServiceDate || null,
      meterType: vehicle.meterType || null,
      currentMeterReading: vehicle.currentMeterReading ?? null,
      defaultServiceIntervalValue: vehicle.defaultServiceIntervalValue ?? null,
      defaultServiceIntervalUnit: vehicle.defaultServiceIntervalUnit || null,
      notes: vehicle.notes || "",
      isActive: vehicle.isActive ?? true,
    });
  }, [vehicle]);

  if (vehicleLoading) {
    return (
      <DashboardPage>
        <AbsoluteCenterProgressLoader />
      </DashboardPage>
    );
  }

  if (!vehicle) {
    return (
      <DashboardPage>
        <MDBox mb={3}>
          <MDTypography variant="h4" color="error">
            Vehicle not found
          </MDTypography>
        </MDBox>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage>
      {vehicleError && (
        <MDAlert color="error" mb={2}>
          {parseGraphQLError(vehicleError)}
        </MDAlert>
      )}

      <VehicleForm
        key={vehicle.id}
        mode="edit"
        onSubmit={onSubmit}
        loading={updating}
        onCancel={handleCancel}
        initialData={initialData}
        title="Vehicle Details"
        submitButtonText="Update Vehicle"
      />

      <MDBox mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <VehiclePartsSection
              vehicleId={vehicle.id}
              parts={vehicle.parts || []}
              onSaved={refetch}
            />
          </Grid>
          <Grid item xs={12}>
            <VehicleServicePlansSection
              vehicleId={vehicle.id}
              servicePlans={vehicle.servicePlans || []}
              onSaved={refetch}
            />
          </Grid>
          <Grid item xs={12}>
            <VehicleMaintenanceHistorySection vehicleId={vehicle.id} />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardPage>
  );
}
